import type { MapDocument } from "@maps/schema";

interface SnapPoint {
  x: number;
  y: number;
}

/**
 * Simple spatial hash for screen-space vertex snapping.
 * Rebuilt on each document change; queried by the active tool.
 */
export class SnapIndex {
  private points: SnapPoint[] = [];

  rebuild(doc: MapDocument): void {
    this.points = [];
    for (const f of doc.features) {
      if (f.type === "road") {
        for (const v of (f as { centerline: SnapPoint[] }).centerline) {
          this.points.push({ x: v.x, y: v.y });
        }
      } else if (f.type === "building") {
        for (const v of (f as { polygon: SnapPoint[] }).polygon) {
          this.points.push({ x: v.x, y: v.y });
        }
      }
    }
  }

  /** Find nearest snap point within `maxDistPx` (screen-space) of (wx, wy). */
  findNearest(
    worldX: number,
    worldY: number,
    zoom: number,
    maxDistPx: number = 12,
  ): SnapPoint | null {
    const maxDistWorld = maxDistPx / zoom;
    let best: SnapPoint | null = null;
    let bestDist = maxDistWorld;
    for (const p of this.points) {
      const d = Math.hypot(p.x - worldX, p.y - worldY);
      if (d < bestDist) {
        bestDist = d;
        best = p;
      }
    }
    return best;
  }

  /** Add a temporary point (for snapping to in-progress vertices). */
  addPoint(p: SnapPoint): void {
    this.points.push(p);
  }
}